// Cost is the calculator's one canonical value. The five adjustment fields are
// views of Cost measured against MSRP / Invoice — derived on render, never fed
// back into Cost:
//
//     active user input -> Cost -> derived MSRP / Invoice values

const NO_ADJUSTMENTS = {
    msrp_minus: null,
    msrp_plus: null,
    msrp_percentage: null,
    invoice_minus: null,
    invoice_plus: null,
}

// Blank means "not supplied", which is not the same as 0 here — so this returns
// null instead of collapsing to a number the way toMoneyNumber does.
export const toAmount = (value) => {
    if (value === null || value === undefined || value === '') return null

    const number = Number(value)
    return Number.isFinite(number) ? number : null
}

// 0 is what the catalog stores for placeholder rows and what DealContext seeds
// Invoice with, so it reads as "unavailable", never "zero dollars".
export const hasBase = (value) => toAmount(value) > 0

export const round2 = (value) => Math.round(value * 100) / 100

// Money: 2 decimals, never negative — a discount over 100% would go below zero.
const toCost = (value) => round2(Math.max(0, value))

// Cost -> every adjustment field. Absent relationships stay null, never 0: a
// missing Invoice and a Cost equal to Invoice both mean "nothing to show", and
// "- $0" would read as a deliberate zero offset.
export const deriveFromCost = (msrp, invoice, cost) => {
    const costAmount = toAmount(cost)
    if (costAmount === null) return {...NO_ADJUSTMENTS}

    const derived = {...NO_ADJUSTMENTS}

    if (hasBase(msrp)) {
        const msrpAmount = toAmount(msrp)
        const difference = round2(msrpAmount - costAmount)

        if (difference > 0) {
            derived.msrp_minus = difference
            // From the raw difference, so the dollar and percentage forms can't
            // compound each other's rounding.
            derived.msrp_percentage = round2(((msrpAmount - costAmount) / msrpAmount) * 100)
        } else if (difference < 0) {
            // Above MSRP has no percentage form — there is no "+ %" field.
            derived.msrp_plus = Math.abs(difference)
        }
    }

    if (hasBase(invoice)) {
        const difference = round2(toAmount(invoice) - costAmount)

        if (difference > 0) derived.invoice_minus = difference
        else if (difference < 0) derived.invoice_plus = Math.abs(difference)
    }

    return derived
}

// One adjustment field -> a new Cost. Null when the input can't produce one (a
// cleared box, or no base to measure against), which callers read as "leave Cost
// alone" — clearing a box must not erase a Cost the user already committed.
export const costFromInput = (field, value, msrp, invoice) => {
    const amount = toAmount(value)
    if (amount === null) return null

    const base = field.startsWith('invoice') ? invoice : msrp
    if (!hasBase(base)) return null

    const baseAmount = toAmount(base)
    // The field's label carries the direction, so the sign is never the user's
    // to supply: "-2000" in the minus box still means $2,000 below.
    const magnitude = Math.abs(amount)

    switch (field) {
        case 'msrp_minus':
        case 'invoice_minus':
            return toCost(baseAmount - magnitude)
        case 'msrp_plus':
        case 'invoice_plus':
            return toCost(baseAmount + magnitude)
        case 'msrp_percentage':
            return toCost(baseAmount * (1 - magnitude / 100))
        default:
            return null
    }
}

// Cost and its adjustment fields written together so they can't disagree. Blank
// stays '', the shape getInitialCalcState and the API payload already use.
export const costUpdate = (msrp, invoice, cost) => {
    const derived = deriveFromCost(msrp, invoice, cost)

    return {
        cost,
        msrp_minus: derived.msrp_minus ?? '',
        msrp_plus: derived.msrp_plus ?? '',
        msrp_percentage: derived.msrp_percentage ?? '',
        invoice_minus: derived.invoice_minus ?? '',
        invoice_plus: derived.invoice_plus ?? '',
    }
}
